import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Syringe } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import type { FieldErrors, Resolver, SubmitHandler } from "react-hook-form";

import { usePatient } from "@/hooks";
import { useMultiStepFormStore } from "@/stores";
import {
  pharmacyCgmSchema,
  pharmacySensorsOnlySchema,
  productInformationFormSchema,
  sensorsDeliveredCgmNoSchema,
  sensorsDeliveredCgmYesSchema,
  sensorsDeliveredSensorsOnlyNoSchema,
  sensorsDeliveredSensorsOnlyYesSchema,
} from "@/types";
import type { ProductInformationFormType } from "@/types";
import {
  Combobox,
  ControlledRadioGroupCard,
  FormField,
  Input,
  NotQualifyModal,
} from "@/ui";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from "@/ui/common/Select";
import {
  MONTHS,
  PATIENT_DISQUALIFY_REASONS,
  READER_OPTIONS,
  YES_NO_RADIO_OPTIONS,
} from "@/utils";
import { daysSinceFirstOfMonth } from "@/utils/daysSinceFirstOfMonth";
import FormButtons from "./FormButtons";

const dynamicResolver: Resolver<ProductInformationFormType, unknown> = async (
  values,
  context,
  options,
) => {
  let schemaToUse;
  if (values.sensorsDeliveryMethod === "sentToHome") {
    if (values.product === "cgm") {
      schemaToUse =
        values.cancelledWithLastSupplier === "yes"
          ? sensorsDeliveredCgmYesSchema
          : sensorsDeliveredCgmNoSchema;
    } else if (values.product === "sensorsOnly") {
      schemaToUse =
        values.cancelledWithLastSupplier === "yes"
          ? sensorsDeliveredSensorsOnlyYesSchema
          : sensorsDeliveredSensorsOnlyNoSchema;
    }
  } else if (values.sensorsDeliveryMethod === "pickupAtPharmacy") {
    if (values.product === "cgm") {
      schemaToUse = pharmacyCgmSchema;
    } else if (values.product === "sensorsOnly") {
      schemaToUse = pharmacySensorsOnlySchema;
    }
  }
  if (!schemaToUse) {
    schemaToUse = productInformationFormSchema;
  }
  return zodResolver(schemaToUse)(values, context, options);
};

const hasSensorsOnly = (
  data: string,
  _errors: FieldErrors<ProductInformationFormType>,
): _errors is FieldErrors<ProductInformationFormType> => {
  return data === "sensorsOnly";
};

const ProductInformationForm = () => {
  const { goToNextFormStep, setMultiStepFormData, multiStepFormData } =
    useMultiStepFormStore();
  const {
    handleSubmit,
    watch,
    control,
    reset,
    register,
    formState: { errors },
  } = useForm<ProductInformationFormType>({
    resolver: dynamicResolver,
  });
  const productRadioValue = watch("product");
  const deliveryMethodValue = watch("sensorsDeliveryMethod");
  const cancelledWithLastSupplier = watch("cancelledWithLastSupplier");
  const lastSensorsReceivedQty = watch("lastSensorsReceivedQty");
  const lastSensorsReceivedMonth = watch("lastSensorsReceivedMonth");
  const lastSensorsReceivedYear = watch("lastSensorsReceivedYear");

  const [isNotQualifyModalOpen, setIsNotQualifyModalOpen] = useState(false);
  const [disqualifyPatientReason, setDisqualifyPatientReason] = useState("");

  type DisqualifyReason =
    (typeof PATIENT_DISQUALIFY_REASONS)[keyof typeof PATIENT_DISQUALIFY_REASONS];
  const [reasonToDisqualify, setReasonToDisqualify] =
    useState<DisqualifyReason>(
      PATIENT_DISQUALIFY_REASONS.SUPPLIER_NOT_CANCELLED,
    );

  const { useDisqualifyPatient } = usePatient();
  const { mutate: disqualifyPatient, isPending } = useDisqualifyPatient();
  const handleEndIntake = () => {
    if (multiStepFormData?.generalInformationFormData) {
      const { generalInformationFormData } = multiStepFormData;
      const { firstName, lastName, dateOfBirth, email } =
        generalInformationFormData;
      disqualifyPatient({
        firstName,
        lastName,
        dateOfBirth,
        email,
        reasonToDisqualify,
      });
    }
  };

  const onSubmit: SubmitHandler<ProductInformationFormType> = (data) => {
    if (deliveryMethodValue === "sentToHome") {
      if (cancelledWithLastSupplier === "no") {
        setDisqualifyPatientReason(
          "Patient has not cancelled with their last supplier.",
        );
        setReasonToDisqualify(
          PATIENT_DISQUALIFY_REASONS.SUPPLIER_NOT_CANCELLED,
        );
        setIsNotQualifyModalOpen(true);
        return;
      }
      setDisqualifyPatientReason("Must call the patient's current provider.");
      setReasonToDisqualify(
        PATIENT_DISQUALIFY_REASONS.TIME_REQUIREMENT_NOT_MET_FOR_SENSORS,
      );
      const sensorCount = parseInt(lastSensorsReceivedQty, 10);
      const daysSinceOrder = daysSinceFirstOfMonth(
        Number(lastSensorsReceivedMonth),
        Number(lastSensorsReceivedYear),
      );

      if (
        (sensorCount === 2 && daysSinceOrder < 60) ||
        (sensorCount === 6 && daysSinceOrder < 120)
      ) {
        setIsNotQualifyModalOpen(true);
        return;
      }
    }
    if (deliveryMethodValue === "pickupAtPharmacy") {
      setDisqualifyPatientReason("Must call the patient's current provider.");
      setReasonToDisqualify(
        PATIENT_DISQUALIFY_REASONS.TIME_REQUIREMENT_NOT_MET_FOR_SENSORS,
      );
      const sensorCount = parseInt(lastSensorsReceivedQty, 10);
      const daysSincePickUp = daysSinceFirstOfMonth(
        Number(lastSensorsReceivedMonth),
        Number(lastSensorsReceivedYear),
      );
      if (sensorCount === 6 && daysSincePickUp < 60) {
        setIsNotQualifyModalOpen(true);
        return;
      }
    }
    setMultiStepFormData({ productInformationFormData: data });
    goToNextFormStep();
  };

  useEffect(() => {
    if (multiStepFormData?.productInformationFormData) {
      reset(multiStepFormData?.productInformationFormData);
    }
  }, [reset, multiStepFormData?.productInformationFormData]);

  return (
    <div className="flex flex-col gap-10">
      <div className="flex justify-between">
        <div className="flex items-center gap-2">
          <Syringe className="text-primary-600" size={16} />
          <p className="font-extrabold">Product information</p>
        </div>
        <p className="text-sm font-extrabold text-neutral-500">
          All fields are required
        </p>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="grid grid-cols-2 gap-x-12 gap-y-10"
      >
        <FormField
          label="Do you go to the pharmacy, or are your sensors delivered to your home?"
          error={errors.sensorsDeliveryMethod?.message}
        >
          <ControlledRadioGroupCard
            name="sensorsDeliveryMethod"
            control={control}
            options={[
              { value: "sentToHome", label: "Sensors delivered" },
              { value: "pickupAtPharmacy", label: "Pharmacy" },
            ]}
          />
        </FormField>

        {deliveryMethodValue === "sentToHome" ? (
          <FormField
            label="Have you cancelled with your last supplier?"
            error={
              (errors as FieldErrors<{ cancelledWithLastSupplier: string }>)
                .cancelledWithLastSupplier?.message
            }
            className="justify-between"
          >
            <ControlledRadioGroupCard
              name="cancelledWithLastSupplier"
              control={control}
              options={[
                { value: "yes", label: "Yes" },
                { value: "no", label: "No" },
              ]}
            />
          </FormField>
        ) : (
          <div />
        )}

        <FormField label="Product" error={errors.product?.message}>
          <ControlledRadioGroupCard
            name="product"
            control={control}
            options={[
              { value: "cgm", label: "CGM" },
              { value: "sensorsOnly", label: "Sensors only" },
            ]}
          />
        </FormField>
        <div />

        {hasSensorsOnly(productRadioValue, errors) && (
          <>
            <FormField
              label="Which CGM reader were you using?"
              error={
                (errors as FieldErrors<{ cgmReader: string }>).cgmReader
                  ?.message
              }
            >
              <Controller
                control={control}
                name="cgmReader"
                render={({ field, fieldState }) => (
                  <Combobox
                    options={READER_OPTIONS}
                    value={field.value}
                    onChange={field.onChange}
                    hasError={!!fieldState.error?.message}
                    placeholder="Select or search CGM reader"
                  />
                )}
              />
            </FormField>
            <FormField
              label="Do you still have that reader?"
              error={
                (errors as FieldErrors<{ stillHaveReader: string }>)
                  .stillHaveReader?.message
              }
            >
              <ControlledRadioGroupCard
                name="stillHaveReader"
                control={control}
                options={YES_NO_RADIO_OPTIONS}
              />
            </FormField>
            <FormField
              label="When did you start using your CGM?"
              error={(errors as FieldErrors<{ month: string }>).month?.message}
            >
              <div className="grid grid-cols-3 gap-2">
                <Controller
                  control={control}
                  name="month"
                  render={({ field, fieldState }) => (
                    <SelectGroup>
                      <Select
                        value={field.value ? String(field.value) : undefined}
                        onValueChange={(value) => field.onChange(Number(value))}
                      >
                        <SelectTrigger
                          placeholder="Month"
                          className={
                            fieldState.error
                              ? "border-error-500"
                              : "border-neutral-200"
                          }
                        />
                        <SelectContent>
                          {MONTHS.map(({ id, value, label }) => (
                            <SelectItem key={id} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </SelectGroup>
                  )}
                />

                <Input
                  id="year"
                  placeholder="Year"
                  type="number"
                  maxLength={4}
                  error={
                    (errors as FieldErrors<{ year: string }>).year?.message
                  }
                  {...register("year", {
                    setValueAs: (v: string) =>
                      String(v).trim() === "" ? undefined : parseInt(v, 10),
                  })}
                />
              </div>
            </FormField>
            <div />
          </>
        )}

        {cancelledWithLastSupplier === "yes" &&
          deliveryMethodValue === "sentToHome" && (
            <>
              <FormField
                label="Name of last supplier"
                error={
                  (errors as FieldErrors<{ lastSensorsSupplierName: string }>)
                    .lastSensorsSupplierName?.message
                }
              >
                <Input
                  type="text"
                  placeholder="Last supplier"
                  {...register("lastSensorsSupplierName")}
                  error={
                    (errors as FieldErrors<{ lastSensorsSupplierName: string }>)
                      .lastSensorsSupplierName?.message
                  }
                />
              </FormField>
              <FormField
                label="When did you last receive your sensors?"
                error={
                  (errors as FieldErrors<{ lastSensorsReceivedMonth: string }>)
                    .lastSensorsReceivedMonth?.message
                }
              >
                <div className="grid grid-cols-3 gap-2">
                  <Controller
                    control={control}
                    name="lastSensorsReceivedMonth"
                    render={({ field, fieldState }) => (
                      <SelectGroup>
                        <Select
                          value={field.value ? String(field.value) : undefined}
                          onValueChange={(value) =>
                            field.onChange(Number(value))
                          }
                        >
                          <SelectTrigger
                            placeholder="Month"
                            className={
                              fieldState.error
                                ? "border-error-500"
                                : "border-neutral-200"
                            }
                          />
                          <SelectContent>
                            {MONTHS.map(({ id, value, label }) => (
                              <SelectItem key={id} value={value}>
                                {label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </SelectGroup>
                    )}
                  />

                  <Input
                    id="lastSensorsReceivedYear"
                    placeholder="Year"
                    type="number"
                    maxLength={4}
                    error={
                      (
                        errors as FieldErrors<{
                          lastSensorsReceivedYear: string;
                        }>
                      ).lastSensorsReceivedYear?.message
                    }
                    {...register("lastSensorsReceivedYear", {
                      setValueAs: (v: string) =>
                        String(v).trim() === "" ? undefined : parseInt(v, 10),
                    })}
                  />
                </div>
              </FormField>
              <FormField
                label="How many sensors did you receive?"
                error={
                  (errors as FieldErrors<{ lastSensorsReceivedQty: string }>)
                    .lastSensorsReceivedQty?.message
                }
              >
                <ControlledRadioGroupCard
                  name="lastSensorsReceivedQty"
                  control={control}
                  options={[
                    { value: "2", label: "2" },
                    { value: "6", label: "6" },
                  ]}
                />
              </FormField>
            </>
          )}

        {deliveryMethodValue === "pickupAtPharmacy" && (
          <>
            <FormField
              label="When did you last pick up your sensors?"
              error={
                (errors as FieldErrors<{ lastSensorsReceivedMonth: string }>)
                  .lastSensorsReceivedMonth?.message
              }
            >
              <div className="grid grid-cols-3 gap-2">
                <Controller
                  control={control}
                  name="lastSensorsReceivedMonth"
                  render={({ field, fieldState }) => (
                    <SelectGroup>
                      <Select
                        value={field.value ? String(field.value) : undefined}
                        onValueChange={(value) => field.onChange(Number(value))}
                      >
                        <SelectTrigger
                          placeholder="Month"
                          className={
                            fieldState.error
                              ? "border-error-500"
                              : "border-neutral-200"
                          }
                        />
                        <SelectContent>
                          {MONTHS.map(({ id, value, label }) => (
                            <SelectItem key={id} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </SelectGroup>
                  )}
                />

                <Input
                  id="lastSensorsReceivedYear"
                  placeholder="Year"
                  type="number"
                  maxLength={4}
                  error={
                    (errors as FieldErrors<{ lastSensorsReceivedYear: string }>)
                      .lastSensorsReceivedYear?.message
                  }
                  {...register("lastSensorsReceivedYear", {
                    setValueAs: (v: string) =>
                      String(v).trim() === "" ? undefined : parseInt(v, 10),
                  })}
                />
              </div>
            </FormField>
            <FormField
              label="How many sensors did you pick up?"
              error={
                (errors as FieldErrors<{ lastSensorsReceivedQty: string }>)
                  .lastSensorsReceivedQty?.message
              }
            >
              <ControlledRadioGroupCard
                name="lastSensorsReceivedQty"
                control={control}
                options={[
                  { value: "2", label: "2" },
                  { value: "6", label: "6" },
                ]}
              />
            </FormField>
          </>
        )}
        <FormButtons />
      </form>
      <NotQualifyModal
        show={isNotQualifyModalOpen}
        title="Patient does not qualify"
        description={disqualifyPatientReason}
        onClose={() => setIsNotQualifyModalOpen(false)}
        isLoading={isPending}
        handleEndIntake={handleEndIntake}
      />
    </div>
  );
};

export default ProductInformationForm;
